import type { BranchKey } from '@festina-life/api'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { useBaseBranchWorkspaces } from '../../../../hooks/useBaseBranchWorkspaces'
import { server as serverPath } from '../../../../system/paths'
import { useNavigationParams } from '../../../../system/useNavigationParams'
import { isEqualBranch } from '../../../../uiModels/branches/helpers'
import { useIsPersonaEditor } from '../../../../uiModels/useIsPersonaEditor'
import { isEqualWorkspace } from '../../../../workspace/helpers'
import { useOperationsContext } from '../../../../workspace/operationsContext'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'

type Props = {
  branchKey: BranchKey
}

export const NavigateToBranchButton = ({ branchKey }: Props) => {
  const isPersonaEditor = useIsPersonaEditor()
  const translate = useTranslate()
  const navigate = useNavigate()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const { workspaceKey } = useOperationsContext()
  const { block, layer, branch, server } = useNavigationParams()
  const [didChangeBranch, setDidChangeBranch] = useState(false)
  const currentBranchKey = branch ? { name: branch, server } : undefined
  const isCurrentBranch = isEqualBranch(branchKey, currentBranchKey)
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const isUnifiedWorkspaceBranch = branchWorkspaces.length === 1
  const isUnifiedWorkspaceBranchWithOpenWorkspace =
    isUnifiedWorkspaceBranch &&
    isEqualBranch(workspaceKey?.branchKey, branchKey) &&
    isEqualWorkspace(currentWorkspaceKey, workspaceKey)

  const handleChangeBranch = async () => {
    const accesibleWorkspaces = isPersonaEditor ? branchWorkspaces : []
    const singleWorkspace = isUnifiedWorkspaceBranch
      ? accesibleWorkspaces.at(0)
      : undefined

    await navigate(
      serverPath({
        server: branchKey.server,
        branch: branchKey?.name,
        workspaceName: singleWorkspace?.name,
        layer,
        block,
      }),
    )

    setDidChangeBranch(true)
  }

  const isBranchLoading =
    didChangeBranch &&
    (branchKey.name !== branch || branchKey.server !== server)

  useEffect(() => {
    if (didChangeBranch && !isBranchLoading) {
      setDidChangeBranch(false)
    }
  }, [didChangeBranch, isBranchLoading])

  return (
    <Button
      startIcon="ArrowRightEndOnRectangle"
      onClick={handleChangeBranch}
      isLoading={isBranchLoading}
      disabled={
        isUnifiedWorkspaceBranch
          ? isUnifiedWorkspaceBranchWithOpenWorkspace
          : isCurrentBranch
      }
    >
      {translate('branchDetailsPanel.tabs.manage.openBranch')}
    </Button>
  )
}
